import React, { useEffect, useState } from "react";
import Layout from "../../Layout";
import { FiAlertCircle, FiLock, FiSlash, FiTrash2 } from "react-icons/fi";
import { HiOutlinePencil } from "react-icons/hi";
import AddClosedDateModal from "./ClosedDate/AddClosedDateModal";
import AddSpecialDateModal from "./ClosedDate/AddSpecialDateModal";
import ConfirmDeleteModal from "./ClosedDate/ConfirmDeleteModal";

import Loading from "../../Util/Loading";
import rosterApi from "../../api/roster/RosterApi";
import toast from "react-hot-toast";

const ClosedDate = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openSpecial, setOpenSpecial] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const handleSaveSpecial = (data) => {
    console.log("Special date saved:", data);
    setOpenSpecial(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { response } = await rosterApi.getClosedDates();
        setItems(response?.data?.closedDates || []);
      } catch {
        toast.error("Failed to load closed dates");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ---------------- add / edit ---------------- */

  const handleCreateOrUpdate = async (payload) => {
    try {
      setLoading(true);

      // EDIT = remove old date first (RosterSchema key is date-based)
      if (editingItem?.date) {
        await rosterApi.deleteClosedDate(editingItem.date);
      }

      await rosterApi.createClosedDate(payload);

      toast.success(editingItem ? "Closed date updated" : "Closed date added");

      setOpenAdd(false);
      setEditingItem(null);

      const { response } = await rosterApi.getClosedDates();
      setItems(response?.data?.closedDates || []);
    } catch {
      toast.error("Failed to save closed date");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- delete ---------------- */

  const handleDelete = async () => {
    if (!deleteItem?.date) return;

    try {
      setLoading(true);
      await rosterApi.deleteClosedDate(deleteItem.date);
      toast.success("Closed date removed");

      setDeleteItem(null);

      const { response } = await rosterApi.getClosedDates();
      setItems(response?.data?.closedDates || []);
    } catch {
      toast.error("Failed to delete closed date");
    } finally {
      setLoading(false);
    }
  };

  const formatPrettyDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-NZ", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getDurationLabel = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate || startDate);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

    return days === 1 ? "1 day" : `${days} days`;
  };

  return (
    <Layout>
      {loading ? (
        <Loading />
      ) : (
        <div className="mx-auto px-6 py-8 ">
          {/* Header */}
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-yellowlight">
                Closed Dates
              </h1>

              <button
                onClick={() => setOpenAdd(true)}
                className="bg-yellowlight text-yellowdark hover:bg-yellowdark hover:text-white px-5 py-2 rounded-md shadow"
              >
                Add closed date
              </button>
            </div>
            <hr className=" border border-border mt-2" />
            <p className="text-textGray text-sm ">
              List the dates your business is closed for public holidays,
              maintenance or any other reason. Customers will not be able to
              place online bookings during these dates.{" "}
            </p>
          </div>

          {/* List */}
          <div className="rounded-lg shadow divide-y divide-border border-border border text-text">
            {items.length === 0 && (
              <div className="p-6 text-sm text-textGray">No closed dates</div>
            )}

            {items.map((item) => (
              <div
                key={item._id}
                className="flex justify-between items-center px-6 py-4"
              >
                <div className="flex items-center gap-4">
                  <FiLock className="text-textGray" />
                  <div className="flex flex-col">
                    <p className="font-semibold">
                      {formatPrettyDate(item.startDate || item.date)}
                    </p>
                    <p className="text-sm text-textGray">
                      {getDurationLabel(
                        item.startDate || item.date,
                        item.endDate
                      )}
                    </p>
                    <p className="text-xs text-textGray">
                      {item.note || "Business closed"}
                    </p>
                    <p className="flex items-center gap-1 text-xs text-yellowlight">
                      <FiSlash className="text-[12px]" />
                      Business closed
                    </p>{" "}
                  </div>
                </div>

                <div className="flex gap-2">
                  {/* Edit */}
                  <button
                    className="border border-border px-3 py-2 rounded-md text-sm flex items-center gap-1 text-textGray hover:bg-dry"
                    onClick={() => {
                      setEditingItem(item);
                      setOpenAdd(true);
                    }}
                  >
                    <HiOutlinePencil />
                    Edit
                  </button>

                  {/* Delete */}
                  <button
                    className="bg-yellowlight text-yellowdark px-3 py-2 rounded-md hover:bg-yellowdark hover:text-white"
                    onClick={() => setDeleteItem(item)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals (ONLY ONE OPEN AT A TIME) */}
      {openAdd && (
        <AddClosedDateModal
          mode={editingItem ? "edit" : "add"}
          initialData={editingItem}
          onClose={() => {
            setOpenAdd(false);
            setEditingItem(null);
          }}
          onOpenSpecial={() => {
            setOpenAdd(false);
            setEditingItem(null);
            setOpenSpecial(true);
          }}
          onSubmit={handleCreateOrUpdate}
        />
      )}

      {openSpecial && (
        <AddSpecialDateModal
          onClose={() => setOpenSpecial(false)}
          onSave={handleCreateOrUpdate}
        />
      )}

      {deleteItem && (
        <ConfirmDeleteModal
          title="Delete closed date"
          description={`Are you sure you want to delete "${deleteItem.note}"?`}
          onCancel={() => setDeleteItem(null)}
          onConfirm={handleDelete}
        />
      )}
    </Layout>
  );
};

export default ClosedDate;
